import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../lib/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionWarning, setSessionWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);

  // Fonction de déconnexion
  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
    setSessionWarning(false);
    setRemainingTime(0);
    window.location.href = '/login';
  }, []);

  // Mise à jour de l'activité utilisateur
  const updateActivity = useCallback(() => {
    const token = localStorage.getItem('token');
    if (token && !authService.isTokenExpired()) {
      localStorage.setItem('lastActivity', JSON.stringify(Date.now()));
    }
  }, []);

  // Vérification périodique de la session
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsLoading(false);
      return;
    }

    const checkSession = () => {
      if (authService.isTokenExpired()) {
        console.log('🔐 Session expirée - déconnexion automatique');
        logout();
        return;
      }

      const remaining = authService.getRemainingTime();
      setRemainingTime(remaining);

      // Afficher l'avertissement 2 minutes avant expiration pour admins, 5 minutes pour employés
      const warningThreshold = user?.user_type === 'EMPLOYE' ? 5 * 60 : 2 * 60;
      
      if (remaining <= warningThreshold && remaining > 0) {
        setSessionWarning(true);
      } else {
        setSessionWarning(false);
      }

      // Déconnexion automatique si expiré
      if (remaining <= 0) {
        logout();
      }
    };

    // Vérifier immédiatement
    checkSession();

    // Vérifier toutes les 30 secondes
    const interval = setInterval(checkSession, 30000);

    // Nettoyer l'interval au démontage
    return () => clearInterval(interval);
  }, [user, logout]);

  // Détection d'activité utilisateur
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      updateActivity();
    };

    // Ajouter les listeners d'événements pour détecter l'activité
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Nettoyer les listeners
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [updateActivity]);

  // Initialisation au chargement
  useEffect(() => {
    const initAuth = () => {
      const currentUser = authService.getCurrentUser();
      
      if (currentUser && authService.isAuthenticated() && !authService.isTokenExpired()) {
        setUser(currentUser);
      } else if (authService.isAuthenticated()) {
        // Token expiré
        console.log('🔐 Token expiré au chargement - nettoyage');
        logout();
      }
      
      setIsLoading(false);
    };

    initAuth();
  }, [logout]);

  const login = async (email, password) => {
    try {
      const userData = await authService.login(email, password);
      const userInfo = {
        user_type: userData.user_type,
        user_id: userData.user_id,
        role: userData.role,
        entreprise_id: userData.entreprise_id,
        entreprise_nom: userData.entreprise_nom, // Ajouter entreprise_nom
        must_change_password: userData.must_change_password || false
      };
      setUser(userInfo);
      updateActivity();
      return userData;
    } catch (error) {
      throw error;
    }
  };

  const employeeLogin = async (telephone, pin) => {
    try {
      const userData = await authService.employeeLogin(telephone, pin);
      const userInfo = {
        user_type: userData.user_type,
        user_id: userData.user_id,
        entreprise_id: userData.entreprise_id,
        must_change_password: userData.must_change_password || false,
        nom: userData.nom,
        prenom: userData.prenom,
        matricule: userData.matricule
      };
      setUser(userInfo);
      updateActivity();
      return userData;
    } catch (error) {
      throw error;
    }
  };

  const extendSession = () => {
    const token = localStorage.getItem('token');
    if (token && user) {
      // Simuler une reconnexion pour étendre la session
      if (user.user_type === 'EMPLOYE') {
        localStorage.setItem('tokenExpiry', JSON.stringify(Date.now() + 30 * 60 * 1000));
      } else {
        localStorage.setItem('tokenExpiry', JSON.stringify(Date.now() + 15 * 60 * 1000));
      }
      setSessionWarning(false);
      updateActivity();
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const value = {
    user,
    isLoading,
    login,
    employeeLogin,
    logout,
    isAuthenticated: !!user,
    sessionWarning,
    remainingTime,
    extendSession,
    formatTime,
    updateActivity
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      
      {/* Modal d'avertissement de session */}
      {sessionWarning && remainingTime > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <div className="text-center">
              <div className="text-4xl mb-4">⏰</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Session expirée bientôt
              </h3>
              <p className="text-gray-600 mb-4">
                Votre session va expirer dans <strong>{formatTime(remainingTime)}</strong>.
                <br />
                Voulez-vous continuer ?
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={logout}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Se déconnecter
                </button>
                <button
                  onClick={extendSession}
                  className="flex-1 py-2 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Continuer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};